import { ref } from 'vue';
import AnalyticsRepository from '@/repositories/AnalyticsRepository';
import { getAppEnvironment, getRuntimeConfigValue } from '@/app/runtime';
import { routePattern } from '@/utils/pageViewEvent';

const SESSION_STORAGE_KEY = 'profiteia:analytics:session-id';
// Analytics identifiers live OUTSIDE the `pf_` namespace on purpose so they are
// NOT wiped by clearAllPersistedState() on logout: consent must persist across
// logout, and the analytics visitor id is a stable per-device id. They are also
// deliberately separate from the FingerprintJS `visitorId` (which the download
// client uses for its request checksum) so consent revocation can clear the
// analytics identity without breaking downloads.
const CONSENT_KEY = 'profiteia:analytics:consent';
const VISITOR_KEY = 'profiteia:analytics:visitor-id';
const MAX_QUEUE_SIZE = 200;
const MAX_BATCH_SIZE = 50;
const FLUSH_INTERVAL_MS = 5000;

let queue = [];
let flushTimer = null;
let pagehideInstalled = false;
let flushInFlight = false;

// Consent and the visitor id are mirrored into a cookie on the parent domain so
// one decision and one id cover the website -> portal hop (profiteia.io and
// portal.profiteia.io have separate localStorage). Only written after consent.
const CONSENT_COOKIE = 'profiteia_analytics_consent';
const VISITOR_COOKIE = 'profiteia_analytics_vid';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export function sharedCookieDomain(hostname = typeof location !== 'undefined' ? location.hostname : '') {
  if (!/(^|\.)profiteia\.io$/.test(hostname)) {
    return null;
  }
  // portal.profiteia.io -> profiteia.io; portal.acc.profiteia.io -> acc.profiteia.io
  return hostname.replace(/^(portal|www)\./, '');
}

function readSharedCookie(name) {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function writeSharedCookie(name, value) {
  const domain = sharedCookieDomain();
  if (!domain || typeof document === 'undefined') {
    return;
  }
  const lifetime = value === null ? 'max-age=0' : `max-age=${COOKIE_MAX_AGE_SECONDS}`;
  document.cookie = `${name}=${encodeURIComponent(value ?? '')}; domain=.${domain}; path=/; ${lifetime}; SameSite=Lax; Secure`;
}

function readConsent() {
  if (typeof localStorage === 'undefined') {
    return readSharedCookie(CONSENT_COOKIE);
  }
  try {
    return localStorage.getItem(CONSENT_KEY) || readSharedCookie(CONSENT_COOKIE);
  } catch {
    return readSharedCookie(CONSENT_COOKIE);
  }
}

// Reactive so the app shell + consent banner react to grant/revoke immediately.
const consentState = ref(readConsent()); // 'granted' | 'denied' | null (undecided)

export function hasAnalyticsConsent() {
  return consentState.value === 'granted';
}

export function getAnalyticsConsentState() {
  return consentState.value;
}

export function setAnalyticsConsent(granted) {
  const value = granted ? 'granted' : 'denied';
  consentState.value = value;
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(CONSENT_KEY, value);
    } catch {
      // ignore storage failures (private mode etc.) — consent stays in-memory
    }
  }
  writeSharedCookie(CONSENT_COOKIE, value);
  if (granted) {
    persistAttribution();
    startPostHog();
  } else {
    stopPostHog();
    writeSharedCookie(VISITOR_COOKIE, null);
    if (attribution) { delete attribution.experiment_key; delete attribution.experiment_variant; }
    try { sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY); } catch { /* storage unavailable */ }
  }
  if (!granted) {
    // Revoke: drop anything queued and clear analytics identifiers so a stable
    // device/session identity does not survive a withdrawal of consent.
    queue = [];
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(VISITOR_KEY);
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch {
      // best effort
    }
  }
}

/**
 * Reactive consent accessor for components (banner, settings toggle).
 */
export function useAnalyticsConsent() {
  return {
    consent: consentState,
    hasConsent: hasAnalyticsConsent,
    grant: () => setAnalyticsConsent(true),
    deny: () => setAnalyticsConsent(false),
    set: setAnalyticsConsent,
  };
}

function isDevMode() {
  try {
    return process.env.NODE_ENV !== 'production';
  } catch {
    return false;
  }
}

function makeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getSessionId() {
  if (typeof sessionStorage === 'undefined') {
    return makeId();
  }
  let value = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!value) {
    value = makeId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, value);
  }
  return value;
}

/**
 * Analytics-only visitor id. A random per-device id generated lazily (only ever
 * reached once consent is granted, because trackAnalyticsEvent gates first). It
 * is intentionally NOT the FingerprintJS fingerprint — analytics never depends
 * on device fingerprinting.
 */
function getVisitorId() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    let value = localStorage.getItem(VISITOR_KEY) || readSharedCookie(VISITOR_COOKIE);
    if (!value) {
      value = makeId();
    }
    localStorage.setItem(VISITOR_KEY, value);
    writeSharedCookie(VISITOR_COOKIE, value);
    return value;
  } catch {
    return null;
  }
}

/**
 * Coarse browser traffic class. JS-executing agents and automation are counted
 * in our own pipeline (property `traffic_class`) but never sent to PostHog.
 * Crawlers that do not run JavaScript are Cloudflare's job.
 */
export function classifyBrowserTraffic(nav = typeof navigator !== 'undefined' ? navigator : {}) {
  const ua = String(nav.userAgent || '');
  if (nav.webdriver || /HeadlessChrome|PhantomJS|Puppeteer|Playwright|Selenium/i.test(ua)) {
    return 'automation';
  }
  if (/ChatGPT|GPTBot|OAI-SearchBot|Claude|anthropic|Perplexity|Google-Extended|Bytespider|CCBot/i.test(ua)) {
    return 'ai_agent';
  }
  if (/bot|crawler|spider|slurp|facebookexternalhit|preview/i.test(ua)) {
    return 'crawler';
  }
  return 'human';
}

// ---- Attribution (first touch of this page load; memory only, so no consent needed) ----

const ATTRIBUTION_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 's', 'experiment_key', 'experiment_variant'];
const ATTRIBUTION_STORAGE_KEY = 'profiteia:analytics:attribution';
export const SHARED_ARTICLE_EXPERIMENT = 'shared-article-offer';
const MAX_ATTRIBUTION_LENGTH = 200;
let attribution = null;

function attributionFromUrl(href) {
  try {
    const url = new URL(href);
    // Hash-routed portal URLs carry their query inside the hash (#/register?utm_source=…).
    const hashQuery = url.hash.includes('?') ? url.hash.slice(url.hash.indexOf('?')) : '';
    const params = new URLSearchParams(`${url.search}&${hashQuery.replace(/^\?/, '')}`);
    const captured = Object.fromEntries(ATTRIBUTION_KEYS.filter((key) => params.get(key)).map((key) => [key, params.get(key)]));
    const shareId = params.get('s') || params.get('share');
    if (/^[A-Za-z0-9_-]{1,64}$/.test(shareId || '')) captured.s = shareId;
    else delete captured.s;
    return captured;
  } catch {
    return {};
  }
}

/** Capture UTM/share params and the external referrer host once per page load. */
export function captureAttribution(href = typeof location !== 'undefined' ? location.href : '', referrer = typeof document !== 'undefined' ? document.referrer : '') {
  if (attribution) {
    return attribution;
  }
  let referrerHost = null;
  try {
    referrerHost = referrer ? new URL(referrer).hostname : null;
  } catch {
    referrerHost = null;
  }
  const ownHost = sharedCookieDomain();
  const external = referrerHost && !(ownHost && (referrerHost === ownHost || referrerHost.endsWith(`.${ownHost}`)));
  // The website passes its own external referrer on as `ref` (withAttribution).
  let passedReferrer = null;
  try {
    const url = new URL(href);
    const hashQuery = url.hash.includes('?') ? url.hash.slice(url.hash.indexOf('?') + 1) : '';
    passedReferrer = new URLSearchParams(`${url.search.slice(1)}&${hashQuery}`).get('ref');
  } catch {
    passedReferrer = null;
  }
  let saved = {};
  if (hasAnalyticsConsent()) {
    try { saved = JSON.parse(sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}'); } catch { /* storage unavailable */ }
  }
  attribution = {
    ...saved,
    ...attributionFromUrl(href),
    ...(external ? { referrer_host: referrerHost } : passedReferrer ? { referrer_host: passedReferrer } : {}),
  };
  if (!hasAnalyticsConsent() || attribution.experiment_key !== SHARED_ARTICLE_EXPERIMENT || !['control', 'trial_first'].includes(attribution.experiment_variant)) {
    delete attribution.experiment_key;
    delete attribution.experiment_variant;
  }
  persistAttribution();
  return attribution;
}

function persistAttribution() {
  if (!hasAnalyticsConsent() || !attribution) return;
  try { sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution)); } catch { /* storage unavailable */ }
}

/** Called only after the API has validated this article's share link. */
export function setSharedArticleAttribution(shareId) {
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(shareId || '')) return;
  getAttribution().s = shareId;
  persistAttribution();
}

/** Keep the actual rendered experiment assignment with subsequent conversions. */
export function setSharedArticleVariant(variant) {
  const values = getAttribution();
  delete values.experiment_key;
  delete values.experiment_variant;
  if (hasAnalyticsConsent() && ['control', 'trial_first'].includes(variant)) {
    Object.assign(values, { experiment_key: SHARED_ARTICLE_EXPERIMENT, experiment_variant: variant });
  }
  persistAttribution();
}

/** Anonymous analytics identity is attached to a form only after consent. */
export function analyticsHeaders() {
  return hasAnalyticsConsent() ? { 'X-Visitor-Id': getVisitorId(), 'X-Session-Id': getSessionId() } : {};
}

export function trackSharedArticleConversion(conversionType) {
  if (!getAttribution().s) return;
  trackAnalyticsEvent('share_link.conversion_completed', { properties: { conversion_type: conversionType } });
}

/** First-touch attribution for a new account (Scrumble `attribution`), length-capped. */
export function signupAttribution() {
  return Object.fromEntries(
    Object.entries(getAttribution()).map(([key, value]) => [key, String(value).slice(0, MAX_ATTRIBUTION_LENGTH)])
  );
}

export function getAttribution() {
  return attribution || captureAttribution();
}

/** Append the captured attribution to a portal URL (query inside the hash for hash routes). */
export function withAttribution(href, source = getAttribution()) {
  const entries = Object.entries(source || {}).filter(([key]) => ATTRIBUTION_KEYS.includes(key) || key === 'referrer_host');
  if (!entries.length) {
    return href;
  }
  const url = new URL(href);
  const add = (params) => {
    for (const [key, value] of entries) {
      if (!params.has(key)) params.set(key === 'referrer_host' ? 'ref' : key, value);
    }
    return params;
  };
  if (url.hash.startsWith('#/')) {
    const [path, query = ''] = url.hash.slice(1).split('?');
    url.hash = `${path}?${add(new URLSearchParams(query)).toString()}`;
  } else {
    url.search = add(url.searchParams).toString();
  }
  return url.toString();
}

// ---- PostHog: analysis layer, started only after consent ----

let posthog = null;
let posthogStarting = null;
let currentPattern = '';
let sensitiveRoute = false;
const SENSITIVE_PARAM = /token|code|claim|secret|key/i;

function posthogSettings() {
  return {
    key: getRuntimeConfigValue('VUE_APP_POSTHOG_KEY', process.env.VUE_APP_POSTHOG_KEY || ''),
    host: getRuntimeConfigValue('VUE_APP_POSTHOG_HOST', process.env.VUE_APP_POSTHOG_HOST || 'https://eu.i.posthog.com'),
  };
}

/** Token-safe URL: route pattern instead of the resolved hash path, only attribution params. */
function safeCurrentUrl() {
  if (typeof location === 'undefined') {
    return '';
  }
  const params = new URLSearchParams(Object.entries(attributionFromUrl(location.href)));
  const query = params.toString() ? `?${params}` : '';
  return location.hash.startsWith('#/')
    ? `${location.origin}${location.pathname}#${currentPattern || '/'}${query}`
    : `${location.origin}${location.pathname}${query}`;
}

export function sanitizePostHogEvent(event) {
  if (!event) {
    return null;
  }
  if (classifyBrowserTraffic() !== 'human') {
    return null;
  }
  // Replays of pages whose URL carries a secret (share/verify/recover tokens) are never sent.
  if (event.event === '$snapshot' && sensitiveRoute) {
    return null;
  }
  const url = safeCurrentUrl();
  if (event.properties) {
    for (const key of ['$current_url', '$initial_current_url']) {
      if (key in event.properties) event.properties[key] = url;
    }
    if ('$pathname' in event.properties) {
      event.properties.$pathname = currentPattern || (typeof location !== 'undefined' ? location.pathname : '');
    }
  }
  return event;
}

export function startPostHog() {
  const { key, host } = posthogSettings();
  if (!key || !hasAnalyticsConsent() || typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (posthog) {
    posthog.opt_in_capturing();
    // Revocation resets PostHog's anonymous ID; keep the first-party pipeline on that new ID too.
    const distinctId = posthog.get_distinct_id?.();
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(distinctId || '')) {
      try { localStorage.setItem(VISITOR_KEY, distinctId); } catch { /* storage unavailable */ }
      writeSharedCookie(VISITOR_COOKIE, distinctId);
    }
    return Promise.resolve(posthog);
  }
  posthogStarting ||= import('posthog-js')
    .then(({ default: client }) => {
      if (!hasAnalyticsConsent()) return null;
      client.init(key, {
        api_host: host,
        person_profiles: 'identified_only',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        cross_subdomain_cookie: true,
        persistence: 'localStorage+cookie',
        bootstrap: { distinctID: getVisitorId() || undefined },
        session_recording: { maskAllInputs: true },
        before_send: sanitizePostHogEvent,
      });
      posthog = client;
      return client;
    })
    .catch(() => null)
    .finally(() => { posthogStarting = null; });
  return posthogStarting;
}

function stopPostHog() {
  if (posthog) {
    posthog.opt_out_capturing();
    posthog.reset();
  }
}

/** One `$pageview` per logical page; `to` is a vue-router location. */
export function capturePostHogPageview(to) {
  currentPattern = routePattern(to) || to?.path || '';
  sensitiveRoute = Object.keys(to?.params || {}).some((name) => SENSITIVE_PARAM.test(name));
  startPostHog().then((client) => {
    client?.capture('$pageview', {
      $current_url: safeCurrentUrl(),
      route_name: String(to?.name || ''),
      matched_pattern: currentPattern,
      ...getAttribution(),
    });
  });
}

/** Link the device to the account, and the account to its tenant (group analytics). */
export function identifyAnalyticsUser(user) {
  if (!user?.id) {
    return;
  }
  startPostHog().then((client) => {
    if (!client) return;
    const emailDomain = String(user.email || '').split('@')[1]?.toLowerCase() || null;
    client.identify(String(user.id), {
      email_domain: emailDomain,
      is_internal: Boolean(emailDomain && (emailDomain === 'profiteia.io' || emailDomain.endsWith('.profiteia.io'))),
    });
    const tenantId = user.tenantId || user.tenant_id || user.tenant?.id;
    if (tenantId) {
      client.group('tenant', String(tenantId), { tenant_type: user.tenantType || user.tenant_type || user.tenant?.type || null });
    }
  });
}

export function resetAnalyticsUser() {
  posthog?.reset();
}

function ensureFlushHooks() {
  if (typeof window === 'undefined') {
    return;
  }
  if (!flushTimer) {
    flushTimer = window.setInterval(() => {
      flushAnalytics();
    }, FLUSH_INTERVAL_MS);
  }
  if (!pagehideInstalled) {
    window.addEventListener('pagehide', () => {
      flushAnalytics({ beacon: true });
    });
    pagehideInstalled = true;
  }
}

function normalizeEvent(eventName, event = {}) {
  const context = getAttribution();
  const attributionProperties = Object.fromEntries(
    [...ATTRIBUTION_KEYS, 'referrer_host'].filter((key) => key !== 's' && context[key]).map((key) => [key, context[key]])
  );
  return {
    event_id: event.event_id || makeId(),
    event_name: eventName,
    schema_version: 1,
    occurred_at: event.occurred_at || new Date().toISOString(),
    source: 'frontend',
    environment: getAppEnvironment(),
    app: event.app || 'frontend',
    visitor_id: event.visitor_id || getVisitorId(),
    session_id: event.session_id || getSessionId(),
    resource_type: event.resource_type || event.resourceType || null,
    resource_id: event.resource_id || event.resourceId || null,
    dataset_id: event.dataset_id || event.datasetId || null,
    visualization_id: event.visualization_id || event.visualizationId || null,
    tool_id: event.tool_id || event.toolId || null,
    dashboard_id: event.dashboard_id || event.dashboardId || null,
    run_id: event.run_id || event.runId || null,
    task_id: event.task_id || event.taskId || null,
    properties: {
      traffic_class: classifyBrowserTraffic(),
      ...attributionProperties,
      ...(context.s ? { share_link_id: context.s } : {}),
      ...(event.properties || {}),
    },
  };
}

export function trackAnalyticsEvent(eventName, event = {}) {
  if (!eventName || typeof window === 'undefined') {
    return;
  }
  // Consent gate: no non-essential analytics (and no visitor id generation)
  // before the user opts in.
  if (!hasAnalyticsConsent()) {
    return;
  }

  ensureFlushHooks();
  queue.push(normalizeEvent(eventName, event));
  if (queue.length > MAX_QUEUE_SIZE) {
    queue = queue.slice(queue.length - MAX_QUEUE_SIZE);
  }
  if (queue.length >= MAX_BATCH_SIZE) {
    flushAnalytics();
  }
}

export async function flushAnalytics({ beacon = false } = {}) {
  if (!hasAnalyticsConsent()) {
    queue = [];
    return;
  }
  if (flushInFlight || queue.length === 0) {
    return;
  }
  const batch = queue.splice(0, MAX_BATCH_SIZE);
  flushInFlight = true;
  try {
    const response = await AnalyticsRepository.trackBatch(batch, { beacon });
    // Surface backend partial-rejections in dev so taxonomy drift (a new event
    // name/property added on the frontend but not the backend) is caught early.
    // Rejected events are permanently invalid — they are NOT requeued.
    const data = response?.data;
    if (isDevMode() && data && data.rejected) {
      // eslint-disable-next-line no-console
      console.warn('[analytics] backend rejected events:', data.rejected, data.rejections || []);
    }
  } catch {
    // Best-effort analytics: dropped batches must never disturb the UX, and we
    // never retry (avoids infinite retry loops on permanently invalid payloads).
  } finally {
    flushInFlight = false;
  }
}

export function __resetAnalyticsForTests() {
  queue = [];
  flushInFlight = false;
  if (flushTimer && typeof window !== 'undefined') {
    window.clearInterval(flushTimer);
  }
  flushTimer = null;
  pagehideInstalled = false;
  consentState.value = readConsent();
  attribution = null;
  posthog = null;
  posthogStarting = null;
  currentPattern = '';
  sensitiveRoute = false;
}

if (hasAnalyticsConsent()) {
  startPostHog();
}

export function useAnalytics() {
  return {
    track: trackAnalyticsEvent,
    flush: flushAnalytics,
  };
}
